import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIsMountedRef } from '@app/utils/useIsMountedRef';

export type UseApiReturnObject<T> = {
    data: T | null;
    loading: boolean;
    error: Response | null;
};

export type ExecuteReturnType<T> = [T | null, Response | null];

export type UseApiReturnType<T, K> = [UseApiReturnObject<T>, (requestParameters?: K) => Promise<ExecuteReturnType<T>>];

export type UseApiConfig = {
    manual?: boolean;
    refresh?: number;
};

// inspired by https://github.com/simoneb/axios-hooks/blob/master/src/index.js

export const useApi = <T, K>(
    fetcher: (requestParameters: K) => Promise<T>,
    requestParameters: K | null = null,
    config: UseApiConfig = {}
): UseApiReturnType<T, K> => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState<boolean>(!config.manual);
    const [error, setError] = useState<Response | null>(null);

    const isMountedRef = useIsMountedRef();

    const lastPromise = useRef<Promise<ExecuteReturnType<T>> | null>(null);

    const requestParametersCache = useMemo(() => requestParameters, [JSON.stringify(requestParameters)]);
    const configCache = useMemo(() => config, [JSON.stringify(config)]);

    const execute = useCallback(
        (requestParametersOverride: Partial<K> | null = null) => {
            setLoading(true);
            setError(null);
            const promise = new Promise<ExecuteReturnType<T>>((resolve) => {
                fetcher({ ...(requestParametersOverride ?? requestParametersCache ?? {}) } as K)
                    .then((data) => {
                        if (promise !== lastPromise.current) {
                            return;
                        }

                        if (isMountedRef.current) {
                            setData(data);
                            setLoading(false);
                        }
                        resolve([data, null]);
                    })
                    .catch((err: Response) => {
                        if (promise !== lastPromise.current) {
                            return;
                        }

                        // todo: process response to error object
                        if (isMountedRef.current) {
                            // setData(null);
                            setError(err);
                            setLoading(false);
                        }
                        resolve([null, err]);
                    });
            });

            lastPromise.current = promise;

            return promise;
        },
        [fetcher, requestParametersCache, isMountedRef]
    );

    useEffect(() => {
        if (!configCache.manual) {
            execute();
        }
    }, [execute, configCache.manual]);

    useEffect(() => {
        if (!configCache.manual && configCache.refresh) {
            const timer = window.setInterval(() => {
                if (isMountedRef.current) {
                    execute();
                }
            }, configCache.refresh * 1000);
            return () => {
                window.clearInterval(timer);
            };
        }
    }, [execute, isMountedRef, configCache.manual, configCache.refresh]);

    return [{ data, loading, error }, execute];
};
